/**
 * Which system the app is running in, said plainly on every screen off the live system (ISS-032):
 * people kept reporting problems that turned out to be work done on the beta or development site.
 * The live system (`production`) shows nothing; anything unrecognised is treated as a test system,
 * never as live.
 */
export type EnvironmentTone = 'develop' | 'beta' | 'sandbox' | 'local' | 'test';

export interface EnvironmentBanner {
    label: string;
    message: string;
    tone: EnvironmentTone;
    /** Where the real work happens. */
    liveUrl: string;
}

export interface EnvironmentFacts {
    env: string | null | undefined;
    useEmulators?: boolean;
}

export const LIVE_URL = 'https://newgenus.org';

export function environmentBanner({ env, useEmulators }: EnvironmentFacts): EnvironmentBanner | null {
    if (useEmulators) {
        return {
            label: 'LOCAL',
            message: 'Local test system on this computer, not the live system',
            tone: 'local',
            liveUrl: LIVE_URL,
        };
    }
    switch (env) {
        case 'production':
            return null;
        case 'develop':
            return {
                label: 'DEVELOPMENT',
                message: 'Development test system, not the live system',
                tone: 'develop',
                liveUrl: LIVE_URL,
            };
        case 'staging':
            return { label: 'BETA', message: 'Beta test site, not the live system', tone: 'beta', liveUrl: LIVE_URL };
        case 'sandbox':
            return {
                label: 'SANDBOX',
                message: 'Sandbox rehearsal copy, not the live system',
                tone: 'sandbox',
                liveUrl: LIVE_URL,
            };
        default:
            return { label: 'TEST', message: 'Test system, not the live system', tone: 'test', liveUrl: LIVE_URL };
    }
}

/** The browser tab name says it too: "BETA · Newgenus". */
export function environmentTabTitle(title: string, facts: EnvironmentFacts): string {
    const banner = environmentBanner(facts);
    if (!banner) return title;
    const prefix = `${banner.label} · `;
    return title.startsWith(prefix) ? title : prefix + title;
}
